import classnames from 'classnames';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import type { IntlKey } from 'locales';
import { uniqWith } from 'lodash-es';
import { CSSProperties, FC, ReactNode, useCallback, useMemo, useState } from 'react';
import type { Observable } from 'rxjs';

import { SearchInput } from './SearchInput';

export interface SearchResultProps<Item> {
  data: Item[] | ReadonlyArray<Item>;
  searchedText: string | null;

  currentIndex: number | null;
  onSelected: (value: Item) => void;
  getError?: (value: Item) => string[];
}

export type SearchOrigin = 'async' | 'sync';

export interface SearchProps<Item> {
  disabled?: boolean;
  autofocus?: boolean;
  hasSearchInput?: boolean;
  getSearchResult?: (text: string) => Observable<readonly Item[]>;
  onValueSelected: (value: Nullable<Item>) => void;
  syncItems?: Item[] | ReadonlyArray<Item>;
  children?: ReactNode;
  resultRenderer: FC<SearchResultProps<Item>>;
  itemsFilter?: (searchedText: Nullable<string>, item: Item) => boolean;
  deduplicateComparator?: (item1: Item, item2: Item) => boolean;
  showSyncItemsOnEmptySearch?: boolean;
  getErrorForItem?: (item: Item) => string[];
  className?: string;
  style?: CSSProperties;
  size?: 'sm' | 'lg' | 'xl';
  defaultValue?: string;

  placeholder?: IntlKey;

  e2e?: string;
}
const EMPTY_ITEMS = [] as const;
export function SelectSearch<Item>({
  onValueSelected,
  getSearchResult,
  autofocus,
  children,
  syncItems = EMPTY_ITEMS,
  deduplicateComparator,
  itemsFilter,
  resultRenderer,
  disabled,
  showSyncItemsOnEmptySearch = true,
  hasSearchInput = true,
  getErrorForItem,
  className,
  style,
  size,
  defaultValue,
  placeholder,
  e2e,
}: SearchProps<Item>) {
  const [asyncData, setData] = useState([] as readonly Item[]);
  const [searchedText, setSearchedText] = useState<Nullable<string>>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  const allItems = useMemo(() => {
    if (showSyncItemsOnEmptySearch && !searchedText) {
      return syncItems;
    }
    const filteredItems = [...asyncData, ...syncItems].filter(item =>
      itemsFilter ? itemsFilter(searchedText, item) : true,
    );
    if (deduplicateComparator) {
      return uniqWith(filteredItems, deduplicateComparator);
    }
    return filteredItems;
  }, [asyncData, syncItems, searchedText, itemsFilter, deduplicateComparator, showSyncItemsOnEmptySearch]);

  const onSearch = useCallback(
    (text: string | undefined) => {
      if (text === undefined || text.length < 3) {
        setSearchedText(null);
        return;
      }
      if (getSearchResult) {
        setSearching(true);
        getSearchResult?.(text).subscribe({
          next: result => {
            setData(result);
            setSearchedText(text);
            setCurrentIndex(result.length > 0 ? 0 : null);
            setSearching(false);
          },
          error: () => {
            setSearching(false);
          },
        });
      }
    },
    [getSearchResult],
  );

  const [_, setSearchedValue] = useDebouncedCallback<string | undefined>(undefined, onSearch, 500);

  const onValidated = useCallback(() => {
    if (allItems.length > 0 && currentIndex !== null) {
      const selectedItem = allItems[currentIndex];
      onValueSelected(selectedItem);
    }
  }, [onValueSelected, allItems, currentIndex]);

  const onItemSelected = useCallback(
    (selectedItem: Item) => {
      onValueSelected(selectedItem);
    },
    [onValueSelected],
  );
  const onMoveCursorUp = useCallback(() => {
    if (asyncData.length > 0 && currentIndex !== null && currentIndex >= 1) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex, asyncData]);

  const onMoveCursorDown = useCallback(() => {
    if (asyncData.length > 0 && currentIndex !== null && currentIndex < asyncData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, asyncData]);

  return (
    <div className={classnames('d-flex flex-column', className)} style={style}>
      {hasSearchInput && (
        <SearchInput
          placeholder={placeholder}
          e2e={e2e}
          size={size}
          disabled={disabled}
          defaultValue={defaultValue}
          onSearchChanged={setSearchedValue}
          onUserValidated={onValidated}
          onMoveCursorUp={onMoveCursorUp}
          onMoveCursorDown={onMoveCursorDown}
          autofocus={autofocus}
          searching={searching}
        >
          {children}
        </SearchInput>
      )}
      {
        resultRenderer({
          data: allItems,
          onSelected: onItemSelected,
          currentIndex,
          searchedText,
          getError: getErrorForItem,
        }) as ReactNode
      }
    </div>
  );
}
